import {
  extendEnumWithNotDisclosed,
  extendEnumWithNotDisclosedAndNotOffered,
  NotDisclosed,
  PreExistingConditionTypeEnum,
} from "@platform/openapi-types";

import { BenefitDefinition } from "../../../benefits/benefit.types";
import { CellSpec } from "../../shared/cellSpecs";
import {
  dollarAmountWithDecimals,
  enumDropdown,
  percentageAmount,
  preExistingConditionMonthsDetail,
} from "../cellSpecs/common";
import {
  currentCommissionType,
  gradedCommissionAmount,
  heapedCommissionAmount,
  requestedCommissionType,
} from "../cellSpecs/commissions";

import {
  applyCellEnumValues,
  createTypeDefinition,
  createValueDefinition,
} from "./builderUtils";

/**
 * Builds all four commission benefit definitions
 * (current type, current amount, requested type, requested amount).
 */
export function buildCommissionBenefits(): Record<string, BenefitDefinition> {
  const currentCommissionTypeDef: BenefitDefinition = {
    label: "Current Commission",
    description: "Current commission type",
    cell: currentCommissionType,
    networkDependent: false,
    // "Unknown" means the commission still has to be found out, so it counts as missing.
    invalidValues: [NotDisclosed],
    refreshesCells: ["currentCommissionAmount"],
  };

  const currentCommissionAmountDef: BenefitDefinition = {
    label: "Amount",
    description: "Current commission amount",
    cell: percentageAmount,
    networkDependent: false,
    nested: true,
    defaultValue: 1,

    editableWhen: {
      dependsOn: "currentCommissionType",
      disabledValues: ["notApplicable", "notDisclosed", "net", "standard"],
    },

    cellVariants: {
      dependsOn: "currentCommissionType",
      options: {
        percentage: percentageAmount,
        pepm: dollarAmountWithDecimals,
        graded: gradedCommissionAmount,
        heaped: heapedCommissionAmount,
      },
      default: percentageAmount,
    },
  };

  const requestedCommissionTypeDef: BenefitDefinition = {
    label: "Requested Commission",
    description: "Requested commission type",
    cell: requestedCommissionType,
    networkDependent: false,
    invalidValues: [NotDisclosed],
    refreshesCells: ["requestedCommissionAmount"],
  };

  const requestedCommissionAmountDef: BenefitDefinition = {
    label: "Amount",
    description: "Requested commission amount",
    cell: percentageAmount,
    networkDependent: false,
    nested: true,
    defaultValue: 1,

    editableWhen: {
      dependsOn: "requestedCommissionType",
      disabledValues: ["notDisclosed", "net", "standard"],
    },

    cellVariants: {
      dependsOn: "requestedCommissionType",
      options: {
        percentage: percentageAmount,
        pepm: dollarAmountWithDecimals,
        graded: gradedCommissionAmount,
        heaped: heapedCommissionAmount,
      },
      default: percentageAmount,
    },
  };

  return {
    currentCommissionType: currentCommissionTypeDef,
    currentCommissionAmount: currentCommissionAmountDef,
    requestedCommissionType: requestedCommissionTypeDef,
    requestedCommissionAmount: requestedCommissionAmountDef,
  } as Record<string, BenefitDefinition>;
}

export interface PreExistingConditionBenefitPairConfig {
  prefix?: string;
  typeLabel?: string;
  typeCell?: CellSpec;
  monthsDetailLabel?: string;
  monthsDetailCell?: CellSpec;
  typeDescription?: string;
  monthsDetailDescription?: string;
  disabledValues?: string[];
  nested?: boolean;
  networkDependent?: boolean;
  typeDefaultValue?: string;
  monthsDetailDefaultValue?: string;
  isRequired?: boolean;
  includeNotOffered?: boolean;
  mpConfigKey?: string;
}

/**
 * Builds a pre-existing condition benefit definition pair.
 *
 * The months detail field's editability depends on the type field's value.
 */
export function buildPreExistingConditionBenefitPair<
  TPrefix extends string = "preExistingCondition",
>(
  config: PreExistingConditionBenefitPairConfig & {
    prefix?: TPrefix;
  } = {} as PreExistingConditionBenefitPairConfig & { prefix?: TPrefix },
): {
  [K in `${TPrefix}Type` | `${TPrefix}MonthsDetail`]: BenefitDefinition;
} {
  const {
    prefix = "preExistingCondition",
    typeLabel = "Pre-Existing Condition",
    typeCell = enumDropdown,
    monthsDetailLabel = "Months Detail",
    monthsDetailCell = preExistingConditionMonthsDetail,
    typeDescription = "Type of pre-existing condition limitation",
    monthsDetailDescription = "Configure months diagnosis free before/after effective date and treatment free. Format: XX/XX or XX/XX/XX",
    disabledValues = ["waived", "none", NotDisclosed],
    nested = true,
    networkDependent = false,
    typeDefaultValue,
    monthsDetailDefaultValue,
    isRequired,
    includeNotOffered = false,
    mpConfigKey,
  } = config;

  const finalTypeCell = applyCellEnumValues(
    typeCell,
    includeNotOffered
      ? extendEnumWithNotDisclosedAndNotOffered(PreExistingConditionTypeEnum)
      : extendEnumWithNotDisclosed(PreExistingConditionTypeEnum),
    typeDefaultValue,
  );

  const typeDef = createTypeDefinition(
    typeLabel,
    typeDescription,
    finalTypeCell,
    networkDependent,
    prefix,
    "MonthsDetail",
    typeDefaultValue,
    isRequired,
  );

  if (mpConfigKey) {
    typeDef.mpConfigKey = mpConfigKey;
  }

  const monthsDetailDef = createValueDefinition(
    monthsDetailLabel,
    monthsDetailDescription,
    monthsDetailCell,
    networkDependent,
    nested,
    prefix,
    disabledValues,
    monthsDetailDefaultValue,
  );

  if (mpConfigKey && !nested) {
    monthsDetailDef.mpConfigKey = mpConfigKey;
  }

  return {
    [`${prefix}Type`]: typeDef,
    [`${prefix}MonthsDetail`]: monthsDetailDef,
  } as {
    [K in `${TPrefix}Type` | `${TPrefix}MonthsDetail`]: BenefitDefinition;
  };
}
